/**
 * SessionParticipantFocus
 *
 * Prioritization-shell wrapper for the participant session experience.
 * Renders the same flow as /sessions/:id (redesign promoted to the only
 * version — DEP-007, 2026-07-07).
 */

import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { ParticipantSessionFlow } from '../../../features/sessions-participant';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';

export function SessionParticipantFocus({ sessionIdProp }: { sessionIdProp?: string }) {
  const params = useParams<{ sessionId: string }>();
  const sessionId = sessionIdProp ?? params.sessionId ?? '';

  const session = useSessionStore(s =>
    sessionId ? Object.values(s.sessions).find(x => x.id === sessionId) ?? null : null,
  );
  const cycle = useDegapProcessStore(s =>
    session?.cycleId ? s.getCycle(session.cycleId) : undefined,
  );

  if (!sessionId) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--text-ter)]" />
      </div>
    );
  }

  const backPath = session?.cycleId
    ? `/prioritization/cycles/${session.cycleId}`
    : '/prioritization/sessions';

  return (
    <div className="min-h-screen">
      {session?.cycleId && (
        <div className="px-6 py-3 border-b border-[var(--rule)] bg-[var(--bg-raised)]">
          <Link
            to={`/prioritization/cycles/${session.cycleId}`}
            className="flex items-center gap-1.5 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]"
          >
            <ArrowLeft className="h-4 w-4" />
            {cycle?.name ?? 'Back to cycle'}
          </Link>
        </div>
      )}
      <ParticipantSessionFlow
        sessionId={sessionId}
        shell="prioritization"
        backPath={backPath}
      />
    </div>
  );
}

export default SessionParticipantFocus;
